import { router, usePage } from "@inertiajs/react"
import { useEffect, useRef } from "react"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import { userPath } from "@/routes"

interface FlashData {
  alert?: string
  notice?: string
  points_awarded?: {
    points: number
    reason?: string
  }
  badge_unlocked?: {
    name: string
    emoji?: string
    reason?: string
  }
  challenge_completed?: {
    title: string
    description: string
    points: number
    icon?: string
  }
}

export const useFlash = () => {
  const page = usePage<{
    auth?: { user?: { id: number } | null }
    flash?: FlashData
  }>()

  const processedFlashRef = useRef<Set<string>>(new Set())
  const lastUserIdRef = useRef<number | null | undefined>(
    page.props.auth?.user?.id,
  )

  // Reset when user changes (login/logout)
  useEffect(() => {
    const currentUserId = page.props.auth?.user?.id
    if (lastUserIdRef.current !== currentUserId) {
      processedFlashRef.current.clear()
      lastUserIdRef.current = currentUserId
    }
  }, [page.props.auth?.user?.id])

  useEffect(() => {
    const showFlashMessages = (flash: FlashData) => {
      // Create a unique key for this flash to avoid duplicates
      const flashKey = JSON.stringify(flash)
      if (processedFlashRef.current.has(flashKey)) return
      processedFlashRef.current.add(flashKey)

      if (flash.alert) {
        toast.error(flash.alert)
      }
      if (flash.notice) {
        toast(flash.notice)
      }

      const points = flash.points_awarded
      const badge = flash.badge_unlocked

      // Si hay puntos y badge juntos (p.ej., tras completar onboarding), escalonamos los toasts
      if (points && badge) {
        // 1) Mostrar puntos primero
        toast(
          <div className="flex items-start gap-3">
            <div className="text-xl" aria-hidden>
              ⭐
            </div>
            <div>
              <div className="font-semibold">+{points.points} puntos</div>
              {points.reason ? (
                <div className="text-muted-foreground text-sm">
                  {points.reason}
                </div>
              ) : null}
            </div>
          </div>,
        )
        // 2) Badge luego de un pequeño delay para que no se tapen
        const userId = page.props.auth?.user?.id
        setTimeout(() => {
          toast(
            <div className="flex flex-col gap-2">
              <div className="text-lg font-semibold">🎉 ¡Nueva insignia!</div>
              <div className="flex items-center gap-3">
                <div className="text-2xl" aria-hidden>
                  {badge.emoji ?? "🏅"}
                </div>
                <div>
                  <div className="font-medium">{badge.name}</div>
                  {badge.reason ? (
                    <div className="text-muted-foreground text-sm">
                      {badge.reason}
                    </div>
                  ) : null}
                </div>
              </div>
              {userId ? (
                <div className="pt-1">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => router.visit(userPath(userId))}
                  >
                    Ver mis insignias
                  </Button>
                </div>
              ) : null}
            </div>,
          )
        }, 900)
        return
      }

      if (points) {
        toast(
          <div className="flex items-start gap-3">
            <div className="text-xl" aria-hidden>
              ⭐
            </div>
            <div>
              <div className="font-semibold">+{points.points} puntos</div>
              {points.reason ? (
                <div className="text-muted-foreground text-sm">
                  {points.reason}
                </div>
              ) : null}
            </div>
          </div>,
        )
      }

      if (badge) {
        const userId = page.props.auth?.user?.id
        toast(
          <div className="flex flex-col gap-2">
            <div className="text-lg font-semibold">🎉 ¡Nueva insignia!</div>
            <div className="flex items-center gap-3">
              <div className="text-2xl" aria-hidden>
                {badge.emoji ?? "🏅"}
              </div>
              <div>
                <div className="font-medium">{badge.name}</div>
                {badge.reason ? (
                  <div className="text-muted-foreground text-sm">
                    {badge.reason}
                  </div>
                ) : null}
              </div>
            </div>
            {userId ? (
              <div className="pt-1">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => router.visit(userPath(userId))}
                >
                  Ver mis insignias
                </Button>
              </div>
            ) : null}
          </div>,
        )
      }

      const challenge = flash.challenge_completed
      if (challenge) {
        toast(
          <div className="flex items-start gap-3">
            <div className="text-2xl" aria-hidden>
              {challenge.icon ?? "🎯"}
            </div>
            <div>
              <div className="font-semibold">{challenge.title}</div>
              <div className="text-muted-foreground text-sm">
                {challenge.description}
              </div>
              <div className="mt-1 text-sm font-medium text-green-600">
                +{challenge.points} puntos bonus
              </div>
            </div>
          </div>,
        )
      }
    }

    // Handle flash from page props whenever it changes
    if (page.props.flash) {
      showFlashMessages(page.props.flash)
    }
  }, [page.props.flash, page.props.auth?.user?.id])
}
